import { Idea, Brainstormer, IdeaComments } from "@/types/brainstormers";
import { ResponsePayload } from "@/types/model";
import {
  getData,
  postData,
  deleteData,
  putData,
  patchData
} from "../base.ehq.model";
import { deserialize } from "../base.serializer";
import { serialize, serializeBAContribution } from "./brainstormer.serializer";

export const getBrainstormer = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  params?: object,
  config?: object
): Promise<Brainstormer> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getBrainstormers = async (
  token: string,
  baseUrl: string,
  projectId: string,
  params?: object,
  config?: object
): Promise<Array<Brainstormer>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getIdeas = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  params?: {
    filterable: boolean;
    page_size: string | number;
    page: string | number;
    filters?: object;
  },
  config?: object
): Promise<Array<Idea>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas`;

  try {
    const res = await getData(token, queryPath, params, config);
    let data = await deserialize(res.data);
    data = {
      ideaList: data,
      meta: res.data.meta,
      page: params.page
    };
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

export const postIdea = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  body: {
    name: string;
    description: string;
    mediaUrl?: string;
    backgroundColourId?: number;
    baItemConfigAttributes?: { unitCap?: number };
  },
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas`;
  const serializedBody = await serialize(body);

  return await postData(token, queryPath, params, serializedBody, config).then(
    (res) => deserialize(res)
  );
};

export const editIdea = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  body: {
    name: string;
    description: string;
    mediaUrl?: string;
    backgroundColourId?: number;
    baItemConfigAttributes?: { unitCap?: number };
  },
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}`;
  const serializedBody = await serialize(body);

  return await patchData(token, queryPath, params, serializedBody, config).then(
    (res: ResponsePayload) => deserialize(res.data)
  );
};

export const postVote = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/votes`;

  return await postData(token, queryPath, params, {}, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const deleteVote = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/votes/1`;

  return await deleteData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getIdea = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getComments = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<Array<IdeaComments>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/comments`;

  try {
    const res = await getData(token, queryPath, params, config);
    const data = await deserialize(res.data);
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

export const postReportIdea = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/report`;

  return await putData(token, queryPath, params, {}, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const postReportIdeaComment = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  commentId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/comments/${commentId}/report`;

  return await putData(token, queryPath, params, {}, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getMentionSuggestions = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/mention_suggestions`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const deleteIdeaComment = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  commentId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/comments/${commentId}/soft_delete`;

  return await patchData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const deleteIdea = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<unknown> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/soft_delete`;

  return await patchData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const getUserBudgetAllocations = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  params?: object,
  config?: object
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/user_allocations`;

  return await getData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const postBAContribution = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  body: {
    unitValue: number;
  },
  params?: object,
  config?: object
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/ba_contribution`;
  const serializedBody = await serializeBAContribution(body);

  return await postData(token, queryPath, params, serializedBody, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};

export const deleteBAContribution = async (
  token: string,
  baseUrl: string,
  projectId: string,
  brainstormerId: string,
  ideaId: string,
  params?: object,
  config?: object
): Promise<any> => {
  const queryPath = `${baseUrl}/projects/${projectId}/brainstormers/${brainstormerId}/ideas/${ideaId}/ba_contribution`;

  return await deleteData(token, queryPath, params, config).then(
    (response: ResponsePayload) => deserialize(response.data)
  );
};
